import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import '../../styles/components/dashboard/PaymentsReceived.css';

const SUMMARY_API = 'https://gp64xgt52i.execute-api.eu-west-2.amazonaws.com/dev/me/payments/summary';
const AMOUNT_KEYS = ['thisMonthCents', 'thisYearCents', 'allTimeCents'];
const periodDate = (value, options) => new Date(value).toLocaleDateString('en-GB', { ...options, timeZone: 'Europe/London' });
const formatMoney = (amount, currency) => {
  const formatter = new Intl.NumberFormat('en-GB', { style: 'currency', currency });
  return formatter.format(amount / (10 ** formatter.resolvedOptions().maximumFractionDigits));
};

const validateSummary = (payload) => {
  if (!payload || payload.timeZone !== 'Europe/London' || !Number.isFinite(Date.parse(payload.asOf))
    || !Array.isArray(payload.currencies)) throw new Error('Invalid payment summary');
  const currencies = new Set();
  payload.currencies.forEach((row) => {
    if (!/^[A-Z]{3}$/.test(row?.currency) || currencies.has(row.currency)
      || AMOUNT_KEYS.some((key) => !Number.isSafeInteger(row[key]) || row[key] < 0)
      || row.thisMonthCents > row.thisYearCents || row.thisYearCents > row.allTimeCents) {
      throw new Error('Invalid payment totals');
    }
    formatMoney(row.allTimeCents, row.currency);
    currencies.add(row.currency);
  });
  return payload;
};

const PaymentsReceived = () => {
  const { authFetch, userAttributes } = useAuth();
  const userSub = userAttributes?.sub;
  const [result, setResult] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const refresh = () => setAttempt((value) => value + 1);
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, []);

  useEffect(() => {
    if (!userSub) return;
    const controller = new AbortController();
    setResult(null);
    const load = async () => {
      try {
        const response = await authFetch(SUMMARY_API, { signal: controller.signal });
        if (!response.ok) throw new Error(`Payment summary request failed (${response.status})`);
        const summary = validateSummary(await response.json());
        if (!controller.signal.aborted) setResult({ userSub, summary });
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('Could not load payments received:', error);
          setResult({ userSub, error: true });
        }
      }
    };
    load();
    return () => controller.abort();
  }, [authFetch, userSub, attempt]);

  const current = result?.userSub === userSub ? result : null;
  const summary = current?.summary;
  const labels = summary ? [
    ['This month', periodDate(summary.asOf, { month: 'long', year: 'numeric' })],
    ['This calendar year', periodDate(summary.asOf, { year: 'numeric' })],
    ['All time', 'Since your first payment'],
  ] : [];

  return (
    <section className="payments-received" aria-labelledby="payments-received-title">
      <h2 id="payments-received-title">Payments received</h2>
      <p className="payments-received__description">Client payments recorded through ViewFinders, before fees and refund adjustments.</p>
      {!current && <p role="status">Loading payments…</p>}
      {current?.error && (
        <div role="alert">
          <p>We could not load your payments.</p>
          <button type="button" onClick={() => setAttempt((value) => value + 1)}>Try again</button>
        </div>
      )}
      {summary?.currencies.length === 0 && <p>No client payments recorded yet.</p>}
      {summary?.currencies.map((row) => (
        <div className="payments-received__currency" key={row.currency}>
          <h3>{row.currency}</h3>
          <dl className="payments-received__totals">
            {AMOUNT_KEYS.map((key, index) => (
              <div key={key}>
                <dt>{labels[index][0]}<span>{labels[index][1]}</span></dt>
                <dd>{formatMoney(row[key], row.currency)}</dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
      {summary && <p className="payments-received__note">Based on payment dates · UK time</p>}
    </section>
  );
};

export default PaymentsReceived;
